import React, { useEffect, useState } from "react";
import { api, fmtUSD, fmtRelative } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Check, X, Play, Terminal, PackageOpen } from "lucide-react";
import { toast } from "sonner";
import RemediationPackageModal from "../components/RemediationPackageModal";

const STATUS_TABS = [
  { value: "all", label: "All" },
  { value: "awaiting_approval", label: "Awaiting" },
  { value: "approved", label: "Approved" },
  { value: "executing", label: "Executing" },
  { value: "completed", label: "Completed" },
  { value: "rejected", label: "Rejected" },
];

const RISK_STYLES = {
  low: "bg-[#dcfce7] text-[#15803d]",
  medium: "bg-[#fef3c7] text-[#92400e]",
  high: "bg-[#fee2e2] text-[#b91c1c]",
};

function StatusPill({ status }) {
  const map = {
    awaiting_approval: { txt: "Awaiting", style: "bg-[#fef3c7] text-[#92400e]" },
    approved: { txt: "Approved", style: "bg-[#dcfce7] text-[#15803d]" },
    rejected: { txt: "Rejected", style: "bg-[#fee2e2] text-[#b91c1c]" },
    executing: { txt: "Executing", style: "bg-[#00579e]/10 text-[#00579e]" },
    completed: { txt: "Completed", style: "bg-[#dcfce7] text-[#15803d]" },
    failed: { txt: "Failed", style: "bg-[#fee2e2] text-[#b91c1c]" },
  };
  const m = map[status] || { txt: status, style: "bg-secondary text-foreground" };
  return (
    <span className={`px-2 py-0.5 rounded-sm text-[11px] font-semibold uppercase tracking-[0.1em] ${m.style}`}>
      {m.txt}
    </span>
  );
}

export default function Remediations() {
  const [status, setStatus] = useState("awaiting_approval");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [viewLog, setViewLog] = useState(null);
  const [pkgFor, setPkgFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.remediations(status);
      setItems(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Refresh every 60s (was 5s — caused jarring re-renders)
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const approve = async (rid) => {
    try {
      await api.approveRemediation(rid);
      toast.success("Remediation approved", { description: "Click Execute to run it (simulated)." });
      load();
    } catch (e) {
      toast.error("Approve failed", { description: e?.response?.data?.detail || e.message });
    }
  };
  const reject = async () => {
    if (!rejecting) return;
    try {
      await api.rejectRemediation(rejecting.id, rejectReason || "Rejected via dashboard");
      toast.success("Remediation rejected");
      setRejecting(null);
      setRejectReason("");
      load();
    } catch (e) {
      toast.error("Reject failed", { description: e?.response?.data?.detail || e.message });
    }
  };
  const execute = async (rid) => {
    try {
      await api.executeRemediation(rid);
      toast.success("Execution started", { description: "Running in SIMULATED mode (demo). Check log shortly." });
      setTimeout(load, 1500);
      setTimeout(load, 3500);
    } catch (e) {
      toast.error("Execute failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="px-6 lg:px-10 py-8 space-y-6" data-testid="remediations-page">
      <Tabs value={status} onValueChange={setStatus}>
        <TabsList className="bg-white border border-border rounded-sm p-1 h-auto">
          {STATUS_TABS.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              data-testid={`tab-${t.value}`}
              className="rounded-sm data-[state=active]:bg-[#00579e] data-[state=active]:text-white text-xs px-3 py-1.5"
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {STATUS_TABS.map((t) => (
          <TabsContent key={t.value} value={t.value} className="mt-6">
            {loading && (
              <div className="cs-card p-10 text-center text-muted-foreground">Loading…</div>
            )}

            {!loading && items.length === 0 && (
              <div className="cs-card p-10 text-center text-muted-foreground" data-testid="no-remediations">
                No remediations in this state.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {!loading && items.map((r) => (
                <div key={r.id} className="cs-card p-5" data-testid={`remediation-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
                        {r.service}
                      </div>
                      <div className="cs-heading text-base font-semibold tracking-tight text-foreground mt-1 truncate">
                        {r.opportunity_type}
                      </div>
                      <div className="cs-mono text-[12px] text-muted-foreground mt-0.5 truncate">
                        {r.resource_name}
                      </div>
                    </div>
                    <StatusPill status={r.status} />
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-[11px] mb-4">
                    <div>
                      <div className="text-muted-foreground uppercase tracking-[0.1em] mb-0.5">Action</div>
                      <div className="cs-mono text-foreground">{r.action}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground uppercase tracking-[0.1em] mb-0.5">Risk</div>
                      <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold ${RISK_STYLES[r.remediation_risk] || ""}`}>
                        {r.remediation_risk}
                      </span>
                    </div>
                    <div>
                      <div className="text-muted-foreground uppercase tracking-[0.1em] mb-0.5">Savings</div>
                      <div className="cs-mono font-semibold text-[#00579e]">{fmtUSD(r.estimated_savings_usd)}/mo</div>
                    </div>
                  </div>

                  <div className="text-[11px] text-muted-foreground mb-3">
                    Project: <span className="cs-mono text-foreground">{r.project_id}</span> · Requested {fmtRelative(r.requested_at)}
                    {r.approved_by && <> · Approved by <span className="text-foreground">{r.approved_by}</span></>}
                    {r.rejected_by && <> · Rejected by <span className="text-foreground">{r.rejected_by}</span></>}
                  </div>

                  {r.status === "awaiting_approval" && (
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        onClick={() => approve(r.id)}
                        data-testid={`approve-${r.id}`}
                        className="rounded-full h-8 bg-[#00579e] hover:bg-[#004580] text-white text-xs px-4"
                      >
                        <Check className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRejecting(r)}
                        data-testid={`reject-${r.id}`}
                        className="rounded-full h-8 border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white text-xs px-4"
                      >
                        <X className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPkgFor(r)}
                        data-testid={`package-${r.id}`}
                        className="rounded-full h-8 text-xs px-4"
                      >
                        <PackageOpen className="w-3.5 h-3.5 mr-1" /> Package
                      </Button>
                    </div>
                  )}

                  {r.status === "approved" && (
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        onClick={() => execute(r.id)}
                        data-testid={`execute-${r.id}`}
                        className="rounded-full h-8 bg-[#00579e] hover:bg-[#004580] text-white text-xs px-4"
                      >
                        <Play className="w-3.5 h-3.5 mr-1" /> Execute (Simulated)
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPkgFor(r)}
                        data-testid={`package-${r.id}`}
                        className="rounded-full h-8 text-xs px-4"
                      >
                        <PackageOpen className="w-3.5 h-3.5 mr-1" /> Package
                      </Button>
                    </div>
                  )}

                  {(r.status === "executing" || r.status === "completed" || r.status === "failed") && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setViewLog(r)}
                      data-testid={`view-log-${r.id}`}
                      className="rounded-sm h-8 text-xs"
                    >
                      <Terminal className="w-3.5 h-3.5 mr-1" /> View Log
                    </Button>
                  )}

                  {r.rejection_reason && (
                    <div className="mt-3 text-[11px] text-muted-foreground border-l-2 border-[#ef4444] pl-2">
                      Reason: {r.rejection_reason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* AutoFix package */}
      <RemediationPackageModal
        open={!!pkgFor}
        onClose={() => setPkgFor(null)}
        opportunityId={pkgFor?.opportunity_id}
        title={pkgFor?.opportunity_type}
      />

      {/* Reject modal */}
      <Dialog open={!!rejecting} onOpenChange={(v) => !v && setRejecting(null)}>
        <DialogContent className="rounded-sm border-border" data-testid="reject-modal">
          <DialogHeader>
            <DialogTitle className="cs-heading">Reject remediation</DialogTitle>
            <DialogDescription>
              Add a short reason. The action will be marked rejected and skipped.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g., Resource owned by Team X — pending migration approval"
            data-testid="reject-reason-textarea"
            className="rounded-sm"
            rows={3}
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setRejecting(null)} className="rounded-sm">Cancel</Button>
            <Button
              onClick={reject}
              data-testid="confirm-reject"
              className="bg-[#ef4444] hover:bg-[#dc2626] text-white rounded-sm"
            >
              Reject
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Log viewer */}
      <Dialog open={!!viewLog} onOpenChange={(v) => !v && setViewLog(null)}>
        <DialogContent className="max-w-2xl rounded-sm border-border" data-testid="log-modal">
          <DialogHeader>
            <DialogTitle className="cs-heading">Execution log</DialogTitle>
            <DialogDescription>
              {viewLog?.action} on <span className="cs-mono">{viewLog?.resource_name}</span>
            </DialogDescription>
          </DialogHeader>
          <pre className="cs-mono text-[11px] bg-[#0B0B0F] text-[#A6E3A1] p-3 rounded-sm max-h-80 overflow-y-auto leading-relaxed">
            {(viewLog?.execution_log || []).join("\n") || "(no log lines yet)"}
          </pre>
          {viewLog?.error && (
            <div className="text-xs text-[#b91c1c]">Error: {viewLog.error}</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
